import { Avatar } from '@systemeio/components'
import { ContactFieldValueInterface, ContactSlugEnum, CountryEnum } from '@systemeio/types'
import {
  getContactFieldTitle,
  useLocalizedCountries,
  useTranslation as useUtilTranslation,
} from '@systemeio/util'
import { CONTACT_FIELDS_SORT_ORDER } from 'modules/contacts/hooks/use-contact-fields'
import { CustomerTypeEnum } from 'modules/profile/user-settings/enums/customer-type-enum'
import React, { type JSX, type ReactNode } from 'react'
import { Tooltip } from 'shared/components'
import { InfoRow } from 'shared/components/info-row'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { usePhoneInput } from 'shared/react-international-phone'
import { dateFormat } from 'shared/utils/date-time-format'

export interface ContactCardInfoInterface {
  avatar?: string
  email: string
  fieldValues: ContactFieldValueInterface[]
  referredBy?: { email?: string } | null
  registeredAt?: string
}

const HEADER_FIELD_SLUGS: string[] = [
  ContactSlugEnum.FirstName,
  ContactSlugEnum.Surname,
  ContactSlugEnum.CompanyName,
]

interface ContactInfoCardProps {
  /** Rows a caller knows about the contact beyond the card's own, placed after them. */
  children?: ReactNode
  contact: ContactCardInfoInterface
  type?: keyof typeof CustomerTypeEnum
}

interface ContactInfoCardTooltipProps {
  children?: JSX.Element | string
  contact: ContactCardInfoInterface
  type?: keyof typeof CustomerTypeEnum
}

export const ContactInfoCard = ({ children, contact, type }: ContactInfoCardProps) => {
  const { t } = useLocoTranslation()
  const { t: tUtil } = useUtilTranslation()
  const { user } = useUser()
  const { getCountryName } = useLocalizedCountries()

  const firstName = contact.fieldValues.find(
    el => el.contactField.slug === ContactSlugEnum.FirstName,
  )?.value
  const lastName = contact.fieldValues.find(
    el => el.contactField.slug === ContactSlugEnum.Surname,
  )?.value
  const companyName = contact.fieldValues.find(
    el => el.contactField.slug === ContactSlugEnum.CompanyName,
  )?.value
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || contact.email

  const phoneNumber = contact.fieldValues.find(
    el => el.contactField.slug === ContactSlugEnum.PhoneNumber,
  )?.value
  const { phone: formattedPhoneNumber } = usePhoneInput({ value: phoneNumber })

  const getSlugValue = (slug: ContactSlugEnum, value: string) => {
    switch (slug) {
      case ContactSlugEnum.Country:
        return Object.values(CountryEnum).includes(value as CountryEnum)
          ? getCountryName(value as CountryEnum)
          : value
      case ContactSlugEnum.PhoneNumber:
        return formattedPhoneNumber
      default:
        return value
    }
  }

  const sortedFields = [...contact.fieldValues]
    .filter(el => !HEADER_FIELD_SLUGS.includes(el.contactField.slug))
    .sort((a, b) => {
      const indexA = CONTACT_FIELDS_SORT_ORDER.indexOf(a.contactField.slug as ContactSlugEnum)
      const indexB = CONTACT_FIELDS_SORT_ORDER.indexOf(b.contactField.slug as ContactSlugEnum)

      if (indexA === -1) return 1
      if (indexB === -1) return -1

      return indexA - indexB
    })

  return (
    <div className="flex w-64 flex-col gap-3 text-start">
      <div className="flex items-center gap-2">
        {/* The backend sends a fallback image when needed, no need to handle it on the dashboard side */}
        {!!contact.avatar && (
          <Avatar
            size="md"
            src={contact.avatar}
            firstName={firstName}
            lastName={lastName}
            alt={fullName}
          />
        )}
        <div className="flex min-w-0 flex-col">
          <span className="text-text-primary truncate">{fullName}</span>
          {companyName && (
            <span className="text-text-tertiary truncate text-sm">{companyName}</span>
          )}
        </div>
      </div>
      <hr className="border-border-200" />
      <div className="flex flex-col gap-2">
        {contact.registeredAt && (
          <InfoRow
            label={t('dashboard.contacts.added')}
            value={dateFormat(contact.registeredAt, {
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          />
        )}
        {sortedFields.map(el => {
          const isKnownField = Object.values(ContactSlugEnum).includes(
            el.contactField.slug as ContactSlugEnum,
          )
          const label = isKnownField
            ? getContactFieldTitle(el.contactField, tUtil)
            : el.contactField.fieldName
          const value = isKnownField
            ? getSlugValue(el.contactField.slug as ContactSlugEnum, el.value)
            : el.value

          return (
            <InfoRow
              key={el.contactField.slug ?? el.contactField.fieldName}
              label={label}
              value={value}
            />
          )
        })}
        {type && <InfoRow label={t('global.type')} value={type} />}
        <InfoRow
          label={t('dashboard.user.affiliate')}
          value={contact.referredBy?.email || t('global.none')}
        />
        {children}
      </div>
    </div>
  )
}

export const ContactInfoCardTooltip = ({
  children,
  contact,
  type,
}: ContactInfoCardTooltipProps) => (
  <Tooltip
    label={<ContactInfoCard contact={contact} type={type} />}
    mode={'hover'}
    triggerProps={{ className: 'w-fit max-w-full truncate' }}
  >
    {children}
  </Tooltip>
)
